import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { NzButtonComponent } from 'ng-zorro-antd/button';
import { NZ_MODAL_DATA, NzModalRef } from 'ng-zorro-antd/modal';
import { MediaCrop, MediaItem } from './media-api';
import { isForbidden, mediaErrorOf, mediaErrorText } from './media-errors';
import { extensionOf, formatClock, formatDimensions, formatSize, pageLang } from './media-format';
import {
  cellCorner,
  CropProblem,
  cropProblem,
  defaultCrop,
  defaultPosterSecond,
  heightForWidth,
  lastSecond,
  layerOf,
  maxZoom,
  movedBy,
  nearestShown,
  normalizedCrop,
  offsetPercent,
  outputInfo,
  photoWidths,
  Region,
  sampleSeconds,
  Size,
  stripLayout,
  StripLayout,
  withOffset,
  withZoom,
  zoomOf
} from './media-frame';
import { MediaIconComponent } from './media-icon.component';
import { MediaPreviewCache } from './media-preview-cache';
import { ProductMediaState } from './product-media.state';

/** What the page hands to the window. */
export interface FrameDialogData {
  state: ProductMediaState;
  itemId: number;
}

/** The box the original is shown in (the board: 560 × 560). */
const STAGE = 560;
const KEY_STEP_PERCENT = 1;
const WHEEL_STEP = 5;

interface Layer {
  width: string;
  left: string;
  top: string;
}

interface Drag {
  x: number;
  y: number;
  crop: MediaCrop;
}

/**
 * The window «Рамка» / «Рамка и обложка» (the board AdmMediaCrop): the original in a box with the frame 375 : 402 over it (drag it, zoom with the slider or the wheel, keys
 * move it), the same frame as the page of the cake, the card of the catalog and the cart will show it, what the files will be, and, for a video, the cover second on the
 * strip. «Сохранить» sends the frame; the item is rendered again from the original.
 */
@Component({
  selector: 'hc-media-frame-dialog',
  imports: [TranslocoDirective, NzButtonComponent, MediaIconComponent],
  providers: [{ provide: MediaPreviewCache, useFactory: () => inject<FrameDialogData>(NZ_MODAL_DATA).state.previews }],
  templateUrl: './media-frame-dialog.component.html',
  styleUrls: ['./media-frame-dialog.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MediaFrameDialogComponent {
  protected readonly stage = STAGE;

  private readonly data = inject<FrameDialogData>(NZ_MODAL_DATA);
  private readonly ref = inject<NzModalRef>(NzModalRef);
  private readonly cache = inject(MediaPreviewCache);
  private readonly translate = inject(TranslocoService);

  /** The item as it was when the window opened. */
  protected readonly item: MediaItem = this.pick();
  protected readonly isVideo = this.item.type === 'VIDEO';
  protected readonly size: Size = { width: this.item.width ?? 0, height: this.item.height ?? 0 };
  protected readonly limits = this.data.state.limits();
  protected readonly maxZoomPercent = maxZoom(this.size);
  protected readonly lastSec = lastSecond(this.item.durationMs ?? 0);
  /** The second of the cover that the server has (the default when nobody chose one). */
  private readonly savedSecond = defaultSecondOf(this.item);
  private readonly start: MediaCrop = this.item.crop ?? defaultCrop(this.size);

  protected readonly crop = signal<MediaCrop>(this.start);
  protected readonly second = signal<number>(this.savedSecond);
  protected readonly saving = signal(false);
  protected readonly error = signal('');
  protected readonly previewSrc = signal<string | null>(null);
  protected readonly stripSrc = signal<string | null>(null);
  protected readonly loadFailed = signal(false);
  private readonly stripNatural = signal<Size | null>(null);
  private drag: Drag | null = null;

  protected readonly problem = computed<CropProblem | null>(() => cropProblem(this.crop(), this.size));
  protected readonly zoom = computed(() => zoomOf(this.crop(), this.size));
  protected readonly offsetX = computed(() => offsetPercent(this.crop(), this.size, 'x'));
  protected readonly offsetY = computed(() => offsetPercent(this.crop(), this.size, 'y'));
  protected readonly scale = Math.min(STAGE / Math.max(1, this.size.width), STAGE / Math.max(1, this.size.height));
  protected readonly stageW = Math.round(this.size.width * this.scale);
  protected readonly stageH = Math.round(this.size.height * this.scale);
  protected readonly frameBox = computed(() => {
    const c = this.crop();
    return { left: c.x * this.scale, top: c.y * this.scale, width: c.w * this.scale, height: c.h * this.scale };
  });

  protected readonly output = computed(() => outputInfo(this.item.type, this.crop().w, this.limits.photoGoodSide));
  protected readonly widthsText = computed(() => this.output().widths.join(', '));
  /** The widths of the cover of a video: it is a picture, made like a photo of the same frame. */
  protected readonly coverWidthsText = computed(() => photoWidths(this.crop().w).join(', '));
  protected readonly secondText = computed(() => formatClock(this.second()));
  /** The video's smaller file of the answer, for «будет только 720 × 772». */
  protected readonly smallest = computed(() => Math.min(...this.output().widths));
  protected readonly smallestH = computed(() => heightForWidth(this.smallest()));

  protected readonly original = computed(() => {
    const parts = [formatDimensions(this.size.width, this.size.height) + ' px', extensionOf(this.item.fileName)];
    if (this.isVideo && this.item.durationMs != null) {
      parts.push(formatClock(this.item.durationMs / 1000));
    }
    if (this.item.fileSize) {
      parts.push(formatSize(this.item.fileSize, pageLang(this.translate.getActiveLang())));
    }
    return parts.filter(Boolean).join(', ');
  });

  // what to draw: the picture of the job, or, for a video at another second than the saved one, the frame of the strip
  private readonly layout = computed<StripLayout | null>(() => {
    const natural = this.stripNatural();
    return natural ? stripLayout(natural, this.size) : null;
  });
  private readonly cellMode = computed(() => {
    if (!this.isVideo || !this.layout() || !this.stripSrc()) {
      return false;
    }
    return this.second() !== this.savedSecond || !this.previewSrc();
  });
  protected readonly stageSrc = computed(() => (this.cellMode() ? this.stripSrc() : this.previewSrc()));
  protected readonly stageLayer = computed<Layer>(() => this.layerFor({ x: 0, y: 0, w: this.size.width, h: this.size.height }));
  protected readonly pageLayer = computed<Layer>(() => this.layerFor(this.crop()));
  protected readonly shownSeconds = computed(() => sampleSeconds(this.lastSec));
  protected readonly shownActive = computed(() => nearestShown(this.shownSeconds(), this.second()));
  /** The frames of the strip shown under the slider: each one is cut out of the strip picture, its middle band, as wide as the cell. */
  protected readonly cells = computed(() =>
    this.shownSeconds().map((sec, i) => {
      const layout = this.layout();
      const natural = this.stripNatural();
      let style: { width: string; left: string; transform: string } | null = null;
      if (layout && natural) {
        const corner = cellCorner(layout, sec);
        const middle = (corner.y + layout.cellH / 2) / natural.height;
        style = {
          width: `${(natural.width / layout.cellW) * 100}%`,
          left: `${(-corner.x / layout.cellW) * 100}%`,
          transform: `translateY(${-middle * 100}%)`
        };
      }
      return { sec, text: formatClock(sec), active: i === this.shownActive(), style };
    })
  );

  constructor() {
    this.cache.get(this.item.previewUrl as string).then(
      url => this.previewSrc.set(url),
      () => this.loadFailed.set(true)
    );
    if (this.isVideo && this.item.stripUrl) {
      this.cache.get(this.item.stripUrl).then(
        url => {
          const probe = new Image();
          probe.onload = () => {
            this.stripNatural.set({ width: probe.naturalWidth, height: probe.naturalHeight });
            this.stripSrc.set(url);
          };
          probe.src = url;
        },
        () => undefined
      );
    }
  }

  // ------------------------------------------------------------------------------------------------------------------------------ the frame

  protected onZoom(event: Event): void {
    this.crop.set(withZoom(this.crop(), this.size, Number((event.target as HTMLInputElement).value)));
  }

  protected onOffset(axis: 'x' | 'y', event: Event): void {
    this.crop.set(withOffset(this.crop(), this.size, axis, Number((event.target as HTMLInputElement).value)));
  }

  protected onSecond(event: Event): void {
    this.second.set(Number((event.target as HTMLInputElement).value));
  }

  protected pickSecond(sec: number): void {
    this.second.set(sec);
  }

  protected onPointerDown(event: PointerEvent): void {
    (event.currentTarget as HTMLElement).setPointerCapture(event.pointerId);
    this.drag = { x: event.clientX, y: event.clientY, crop: this.crop() };
    event.preventDefault();
  }

  protected onPointerMove(event: PointerEvent): void {
    if (this.drag) {
      this.crop.set(movedBy(this.drag.crop, this.size, (event.clientX - this.drag.x) / this.scale, (event.clientY - this.drag.y) / this.scale));
    }
  }

  protected onPointerUp(): void {
    this.drag = null;
  }

  protected onWheel(event: WheelEvent): void {
    event.preventDefault();
    this.crop.set(withZoom(this.crop(), this.size, this.zoom() + (event.deltaY < 0 ? WHEEL_STEP : -WHEEL_STEP)));
  }

  protected onKey(event: KeyboardEvent): void {
    const step = (event.shiftKey ? 10 : 1) * Math.max(1, Math.round((this.size.width * KEY_STEP_PERCENT) / 100));
    const move: Record<string, [number, number]> = { ArrowLeft: [-step, 0], ArrowRight: [step, 0], ArrowUp: [0, -step], ArrowDown: [0, step] };
    const delta = move[event.key];
    if (delta) {
      this.crop.set(movedBy(this.crop(), this.size, delta[0], delta[1]));
    } else if (event.key === '+' || event.key === '=') {
      this.crop.set(withZoom(this.crop(), this.size, this.zoom() + WHEEL_STEP));
    } else if (event.key === '-') {
      this.crop.set(withZoom(this.crop(), this.size, this.zoom() - WHEEL_STEP));
    } else {
      return;
    }
    event.preventDefault();
  }

  /** «Сбросить»: the widest frame, centred, and the cover the server would take. */
  protected reset(): void {
    this.crop.set(defaultCrop(this.size));
    this.second.set(defaultPosterSecond(this.item.durationMs ?? 0));
  }

  protected close(): void {
    this.ref.close(false);
  }

  protected async save(): Promise<void> {
    if (this.saving() || this.problem() !== null) {
      return;
    }
    this.saving.set(true);
    this.error.set('');
    try {
      const posterMs = this.isVideo ? (this.second() !== this.savedSecond ? this.second() * 1000 : (this.item.posterMs ?? undefined)) : undefined;
      await this.data.state.saveFrame(this.item.id, normalizedCrop(this.crop(), this.size), posterMs ?? undefined);
      this.ref.close(true);
    } catch (e) {
      const err = mediaErrorOf(e);
      this.error.set(
        isForbidden(e)
          ? this.translate.translate('productDefinitions.media.errors.forbidden')
          : err
            ? mediaErrorText((key, params) => this.translate.translate(key, params), err.message, err.data, pageLang(this.translate.getActiveLang()))
            : this.translate.translate('productDefinitions.media.errors.failed')
      );
    } finally {
      this.saving.set(false);
    }
  }

  // ------------------------------------------------------------------------------------------------------------------------------ pictures

  /** Where to put the picture inside a box that shows `region` of the original (pixels of the original). */
  private layerFor(region: Region): Layer {
    if (this.cellMode()) {
      const layout = this.layout() as StripLayout;
      const natural = this.stripNatural() as Size;
      const corner = cellCorner(layout, this.second());
      const k = layout.cellW / Math.max(1, this.size.width);
      return layerOf(natural, { x: corner.x + region.x * k, y: corner.y + region.y * k, w: region.w * k, h: region.h * k });
    }
    return layerOf(this.size, region);
  }

  private pick(): MediaItem {
    const item = this.data.state.list()?.items.find(i => i.id === this.data.itemId);
    if (!item || item.width == null || item.height == null || !item.previewUrl) {
      throw new Error('the item cannot be framed yet');
    }
    return item;
  }
}

/** The whole second of the cover the server has for an item: the one chosen, else the default. */
function defaultSecondOf(item: MediaItem): number {
  const duration = item.durationMs ?? 0;
  const ms = item.posterMs;
  return ms != null ? Math.min(lastSecond(duration), Math.max(0, Math.round(ms / 1000))) : defaultPosterSecond(duration);
}
